import { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { Icon } from '@iconify/react'

import Header from '../../components/Header/Header'
import AppTable from '../../components/AppTable/AppTable'
import './POSIntegrationPage.scss'
import FilterSideModal from '../../components/FilterSideModal/FilterSideModal'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import DateRangeDropdown from '../../components/DateRangeDropdown/DateRangeDropdown'
import merchantApi from '../../services/merchantApi'

const DEFAULT_SUMMARY = {
    errorsToday: 0,
    errorsVsYesterday: 0,
    syncEvents: 0,
    successRate: 0,
    webhooksSent: 0,
}

const DEFAULT_PAGINATION = {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: 0,
    to: 0,
}

const asNumber = (value) => {
    const number = Number(
        typeof value === 'string'
            ? value.replace(/[^0-9.-]/g, '')
            : value
    )

    return Number.isFinite(number) ? number : 0
}

const normalizeSummary = (response) => {
    const data = response?.data || response || {}

    return {
        errorsToday: asNumber(data.errors_today ?? data.today_errors ?? data.error_count),
        errorsVsYesterday: asNumber(data.errors_vs_yesterday ?? data.error_change),
        syncEvents: asNumber(data.sync_events_24h ?? data.sync_events ?? data.total_sync_events),
        successRate: asNumber(data.success_rate ?? data.sync_success_rate),
        webhooksSent: asNumber(data.webhooks_sent ?? data.total_webhooks),
    }
}

const formatDateTime = (value) => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)

    return date.toLocaleString('en-MY', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const normalizeLogs = (response) => {
    console.log("responseeee", response)
    const responseData = response?.data
    const data = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
            ? responseData.data
            : []
    const meta = responseData?.pagination || responseData?.meta || {}
    const filters = response?.filters || responseData?.filters || {}

    return {
        rows: data.map((log, index) => {
            const status = log.status_code ?? log.http_status ?? log.status
            const latency = asNumber(log.duration_ms ?? log.response_time_ms ?? log.latency)

            return {
                ...log,
                id: log.id ?? `${log.created_at || 'log'}-${index}`,
                method: String(log.http_method || log.method || '-').toUpperCase(),
                endpoint: log.endpoint || log.path || log.url || '-',
                status,
                merchantCode: log.merchant_code || log.merchant?.merchant_code || '-',
                merchant: log.merchant_name || log.merchant?.name || '-',
                note: log.note || log.message || log.description || '-',
                noteType: status >= 500
                    ? 'error'
                    : status >= 400
                        ? 'warning'
                        : '',
                latency: `${latency.toLocaleString('en-MY')}ms`,
                time: formatDateTime(log.created_at || log.occurred_at || log.timestamp),
            }
        }),
        pagination: {
            total: meta?.total || 0,
            last_page: meta?.last_page || 1,
            current_page: meta?.current_page || 1,
            from: meta?.from || 0,
            to: meta?.to || 0,
        },
        merchantOptions: Array.isArray(filters.merchants)
            ? filters.merchants.map((merchant) => ({
                value: merchant.id ?? merchant.value,
                label: merchant.name ?? merchant.label,
            }))
            : [],
    }
}

const statusTone = (status) => {
    if (status >= 500) return 'error'
    if (status >= 400) return 'warning'
    return 'success'
}

export default function POSIntegrationPage() {
    const [filter, setFilter] = useState({})
    const [searchQuery, setSearchQuery] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [filterSideModalOpen, setFilterSideModalOpen] = useState(false)
    const [summary, setSummary] = useState(DEFAULT_SUMMARY)
    const [rows, setRows] = useState([])
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
    const [merchantOptions, setMerchantOptions] = useState([])
    const [methodOptions, setMethodOptions] = useState([])
    const [periodOptions, setPeriodOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        const loadSummary = async () => {
            try {
                const [summaryResponse, generalResponse] = await Promise.all([
                    merchantApi.posIntegrations.summary(),
                    merchantApi.general.get(),
                ])
                setSummary(normalizeSummary(summaryResponse))
                setMerchantOptions(generalResponse?.data?.merchants || [])
                setMethodOptions(generalResponse?.data?.http_methods || [])
                setPeriodOptions(generalResponse?.data?.pos_integration_periods || [])
            } catch (requestError) {
                setError(requestError?.message || 'Unable to load POS integration summary.')
            }
        }

        loadSummary()
    }, [])

    useEffect(() => {
        const timeoutId = window.setTimeout(async () => {
            setLoading(true)
            setError('')

            const customRange = filter.custom_date_range || {}

            try {
                const response = await merchantApi.posIntegrations.logs({
                    search: searchQuery.trim(),
                    'merchant_ids[]': Array.isArray(filter.merchant)
                        ? filter.merchant
                        : filter.merchant
                            ? [filter.merchant]
                            : undefined,
                    event_type: filter.event_type,
                    http_method: filter.http_method || filter.type,
                    min_latency: asNumber(filter.min_latency) || undefined,
                    date_range: filter.date_range,
                    from: customRange.start,
                    to: customRange.end,
                    page: currentPage,
                    per_page: 10,
                })
                const normalized = normalizeLogs(response)
                setRows(normalized.rows)
                setPagination(normalized.pagination)
                if (normalized.merchantOptions.length) {
                    setMerchantOptions(normalized.merchantOptions)
                }
            } catch (requestError) {
                setRows([])
                setError(requestError?.message || 'Unable to load POS integration logs.')
            } finally {
                setLoading(false)
            }
        }, searchQuery ? 350 : 0)

        return () => window.clearTimeout(timeoutId)
    }, [
        currentPage,
        filter.merchant,
        filter.event_type,
        filter.http_method,
        filter.type,
        filter.min_latency,
        filter.date_range,
        filter.custom_date_range,
        searchQuery,
    ])

    const columns = useMemo(() => [
        {
            field: 'method',
            header: 'Method',
            width: 78,
            render: (row) => <span className={`pos-method pos-method--${row?.method?.toLowerCase()}`}>{row.method}</span>,
        },
        { field: 'endpoint', header: 'Endpoint', minWidth: 300 },
        {
            field: 'status',
            header: 'Status',
            width: 76,
            render: (row) => <span className={`pos-status pos-status--${statusTone(row.status)}`}>{row.status}</span>,
        },
        {
            field: 'merchant',
            header: 'Merchant',
            minWidth: 225,
            render: (row) => (
                <span className="pos-merchant">
                    <span className="pos-merchant__code">{row.merchantCode}</span>
                    <span>{row.merchant}</span>
                </span>
            ),
        },
        {
            field: 'note',
            header: 'Note',
            minWidth: 250,
            render: (row) => <span className={row.noteType ? `pos-note pos-note--${row.noteType}` : 'pos-note'}>{row.note}</span>,
        },
        { field: 'latency_ms', header: 'Latency', width: 95, render: (row) => <span>{row.latency_ms} ms</span> },
        { field: 'time_ago', header: 'Time', width: 85 },
    ], [])

    console.log("filter", filter)

    return (
        <Box className="pos-integration-page">
            <Header items={[{ label: 'Cuscapi', to: '/' }, { label: 'POS integrations', current: true }]} />

            <div className="pos-integration-page__heading">
                <div>
                    <h1>POS integrations</h1>
                    <p>WAVE API call audit log - all merchants</p>
                </div>
                <button type="button" className="pos-integration-page__filter-button" onClick={() => setFilterSideModalOpen(true)}>
                    <Icon icon="ph:funnel-simple" height={17} />
                    Filters
                </button>
            </div>

            <div className="pos-summary">
                <div className="pos-summary__card">
                    <span className="pos-summary__label">Errors today</span>
                    <strong className="pos-summary__value pos-summary__value--error">{summary.errorsToday.toLocaleString()}</strong>
                    <span className="pos-summary__meta pos-summary__meta--error">
                        <Icon icon={summary.errorsVsYesterday >= 0 ? 'ph:arrow-up' : 'ph:arrow-down'} height={14} />
                        {Math.abs(summary.errorsVsYesterday).toLocaleString()} vs yesterday
                    </span>
                </div>
                <div className="pos-summary__card">
                    <span className="pos-summary__label">Sync events (24h)</span>
                    <strong className="pos-summary__value">{summary.syncEvents.toLocaleString()}</strong>
                    <span className="pos-summary__meta">{summary.successRate.toLocaleString()}% success</span>
                </div>
                <div className="pos-summary__card">
                    <span className="pos-summary__label">Webhooks sent</span>
                    <strong className="pos-summary__value">{summary.webhooksSent.toLocaleString()}</strong>
                </div>
            </div>

            {error && <div className="sr-error">{error}</div>}
            <AppTable
                columns={columns}
                rows={rows}
                rowKey="id"
                pagination={{ ...pagination, current_page: currentPage }}
                loading={loading}
                onPageChange={(page) => setCurrentPage(page)}
                toolBar={(
                    <div className="pos-toolbar">
                        <div className="pos-toolbar__filters">
                            <FilterDropdown
                                label={'merchants'}
                                options={merchantOptions}
                                value={filter.merchant}
                                onChange={(value) => {
                                    setFilter({ ...filter, merchant: value })
                                    setCurrentPage(1)
                                }}
                            />
                            <FilterDropdown
                                label={'types'}
                                options={methodOptions}
                                value={filter.http_method}
                                onChange={(value) => {
                                    setFilter({ ...filter, http_method: value })
                                    setCurrentPage(1)
                                }}
                            />
                            <DateRangeDropdown
                                value={filter.date_range}
                                options={periodOptions}
                                customRange={filter.custom_date_range}
                                onChange={(value) => {
                                    setFilter({ ...filter, date_range: value })
                                    setCurrentPage(1)
                                }}
                                onCustomRange={(value) => {
                                    setFilter({ ...filter, custom_date_range: value, date_range: 'custom' })
                                    setCurrentPage(1)
                                }}
                            />
                            <button
                                type="button"
                                className="pos-toolbar__clear"
                                onClick={() => {
                                    setFilter({})
                                    setSearchQuery('')
                                    setCurrentPage(1)
                                }}
                            >
                                Clear all
                            </button>
                        </div>
                        <label className="pos-toolbar__search">
                            <Icon icon="ph:magnifying-glass" height={17} />
                            <input
                                value={searchQuery}
                                onChange={(event) => {
                                    setSearchQuery(event.target.value)
                                    setCurrentPage(1)
                                }}
                                placeholder="Search events"
                            />
                        </label>
                    </div>
                )}
                emptyText="No integration events found."
            />
            <FilterSideModal
                open={filterSideModalOpen}
                onClose={() => setFilterSideModalOpen(false)}
                values={filter}
                onChange={(key, value) => setFilter((current) => ({ ...current, [key]: value }))}
                onApply={() => { setFilterSideModalOpen(false) }}
                onReset={() => setFilter({})}
                filters={getFilterList(merchantOptions, methodOptions, periodOptions)}
            />
        </Box>
    )
}

const getFilterList = (merchantOptions, methodOptions, periodOptions) => [
    {
        key: 'merchant',
        label: 'Merchant',
        type: 'checked-list',
        options: merchantOptions,
        placeholder: 'Select merchant',
    },
    {
        key: 'event_type',
        label: 'Event Type',
        type: 'chip',
        options: [
            { label: 'Error', value: 'error' },
            { label: 'Sync', value: 'sync' },
            { label: 'Webhook', value: 'webhook' },
            { label: 'Auth', value: 'auth' },
        ],
    },
    {
        key: 'http_method',
        label: 'HTTP Method',
        type: 'chip',
        options: methodOptions,
    },
    {
        key: 'min_latency',
        label: 'Min Latency (ms)',
        type: 'chip',
        options: [
            // { label: 'Any', value: 'any' },
            { label: '> 500ms', value: '>500ms' },
            { label: '>1,000ms', value: '>1000ms' },
            { label: '>3,000ms', value: '>3000ms' },
        ],
    },
    {
        key: 'date_range',
        label: 'Date Range',
        type: 'chip',
        options: periodOptions,
    },
    {
        key: 'custom_date_range',
        label: '',
        type: 'date-range',
        showWhen: {
            key: 'date_range',
            equals: 'custom',
        },
    },
]
